/**
 * The music director (07 §4). Two sequencers: a looping BED (the title theme in the menus, the
 * layered mission song in the field) and a one-shot STING over it (the touchdown reprise, the
 * debrief cues, medal, promotion, memorial), under which the bed ducks.
 *
 * Layers change at bar lines; the mission's three hard cuts do not wait for one. The bed is
 * (re)started from bar 1 at the first frame of the mission and again at touchdown, and it stops
 * dead at the jump - "cuts to wind only under canopy".
 *
 * With the "1987 score" option the bed's layers all stay off, so a mission has no music but
 * the touchdown reprise - as the original had - and its functional sound carries the tension.
 */

import type { MissionDef, Outcome, World } from '../../sim/types';
import { clockUrgent } from '../mix';
import { activeLayers, createLayerMask, type ScoreState } from '../music/layers';
import { missionSong } from '../music/mission';
import type { Song } from '../music/pattern';
import { type StingId, STINGS } from '../music/stings';
import { TITLE_THEME, TOUCHDOWN_REPRISE } from '../music/title';
import type { Bank } from './bank';
import type { Graph } from './graph';
import { Sequencer } from './player';

const DUCKED = 0.25;

export class Score {
  private readonly bed: Sequencer;
  private readonly sting: Sequencer;
  private readonly mask = createLayerMask();
  private readonly state: ScoreState = {
    phase: 'ground', alert: 0, urgent: false, osprey: 'away', objectiveComplete: false, ended: false, score1987: false,
  };
  private mission: Song | null = null;
  /** Start the mission bed on the next `update`, once the layer mask reflects this frame's world. */
  private startBed = false;
  private stingPlaying = false;
  private readonly restore = (): void => {
    this.stingPlaying = false;
    this.bed.duck(1);
  };

  constructor(graph: Graph, bank: Bank) {
    this.bed = new Sequencer(graph, bank);
    this.sting = new Sequencer(graph, bank);
  }

  /** From the timer and from `update`: whichever comes first keeps the look-ahead window full. */
  tick(): void {
    this.bed.tick();
    this.sting.tick();
  }

  playTitle(): void {
    if (this.bed.songName === TITLE_THEME.name) return;
    this.mission = null;
    this.startBed = false;
    this.bed.mask = null;
    this.bed.play(TITLE_THEME, true);
  }

  stop(): void {
    this.startBed = false;
    this.stingPlaying = false;
    this.bed.stop();
    this.sting.stop();
  }

  beginMission(def: MissionDef): void {
    this.stop();
    this.mission = missionSong(def.biome);
    this.startBed = true;
  }

  update(world: World, score1987: boolean): void {
    const s = this.state;
    s.phase = world.insertion.phase;
    s.alert = world.alert;
    s.urgent = clockUrgent(world.clockTicks, s.phase === 'ground');
    s.osprey = world.osprey.phase;
    s.objectiveComplete = world.objective.complete;
    s.ended = world.result !== null;
    s.score1987 = score1987;
    activeLayers(s, this.mask);
    if (this.startBed && this.mission && !s.ended) {
      this.startBed = false;
      this.bed.mask = this.mask;
      this.bed.play(this.mission, true);
      if (this.stingPlaying) this.bed.duck(DUCKED);
    }
  }

  private playSting(song: Song): void {
    this.stingPlaying = true;
    this.bed.duck(DUCKED);
    this.sting.mask = null;
    this.sting.play(song, false, this.restore);
  }

  /** Out of the aircraft: the snare roll stops dead, and the descent is wind only. */
  jumped(): void {
    this.bed.stop();
  }

  /** Boots on the ground: the one musical moment every player of the original remembers (01 §10). */
  touchdown(): void {
    this.playSting(TOUCHDOWN_REPRISE);
    this.startBed = true;
  }

  cue(id: StingId): void {
    // Nothing marches under the memorial: the bed stops, and the menus restart the theme when they want it.
    if (id === 'memorial') this.bed.stop();
    this.playSting(STINGS[id]);
  }

  /** The mission has a result: the layers stop and the debrief cue takes over. */
  ended(outcome: Outcome, objectiveComplete: boolean): void {
    this.startBed = false;
    this.bed.stop();
    const id: StingId = outcome === 'kia' ? 'kia'
      : outcome === 'scrubbed' ? 'scrubbed'
      : outcome === 'captured' || !objectiveComplete ? 'loss' : 'success';
    this.sting.mask = null;
    this.sting.play(STINGS[id], false, null);
  }

  /** Back to the menus: the mission bed stops; a debrief cue still ringing is left to finish. */
  endMission(): void {
    this.startBed = false;
    this.mission = null;
    if (this.bed.songName !== TITLE_THEME.name) this.bed.stop();
  }
}
